import {
  cloneElement,
  forwardRef,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import { clsx } from "@alpacahq/ui/old";

interface MessageProps {
  errorMsg?: ReactNode;
  successMsg?: ReactNode;
}

type BaseInputProps = InputHTMLAttributes<HTMLInputElement> &
  MessageProps & {
    label?: string;
    helpMsg?: ReactNode;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    status?: "success" | "error" | "neutral";
  };

type SuccessInputProps = BaseInputProps & {
  successMsg: ReactNode;
  errorMsg?: never;
};

type ErrorInputProps = BaseInputProps & {
  errorMsg: ReactNode;
  successMsg?: never;
};

export type InputProps = BaseInputProps | SuccessInputProps | ErrorInputProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      disabled,
      successMsg,
      errorMsg,
      helpMsg,
      leftIcon,
      rightIcon,
      status = "neutral",
      className,
      ...props
    },
    ref,
  ) => {
    // determine the status of the input
    const autoStatus = errorMsg ? "error" : successMsg ? "success" : "neutral";
    const finalStatus = status === "neutral" ? autoStatus : status;

    // styles for each status
    const styles = {
      error: {
        label: "text-red-600",
        input:
          "bg-white border border-red-500 text-red-900 placeholder-red-600 focus:ring-1 focus:ring-red-500 focus:border-red-500",
        icon: "text-red-600",
      },
      success: {
        label: "text-emerald-600 dark:text-emerald-400",
        input:
          "bg-white border border-emerald-500 text-emerald-900 placeholder-emerald-600 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500",
        icon: "text-emerald-500",
      },
      neutral: {
        label: "text-zinc-900",
        input:
          "bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-500 focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400",
        icon: "text-gray-500",
      },
    };

    // get the style based on status for the input
    const style = styles[finalStatus];

    // wrapper for icon with position classes
    const IconWrapper = ({
      children,
      position,
    }: {
      children: ReactElement;
      position: "left" | "right";
    }) => {
      const childClassName =
        typeof children.props === "object" &&
        children.props &&
        "className" in children.props &&
        typeof children.props.className === "string"
          ? children.props.className
          : undefined;

      return cloneElement(children, {
        className: clsx(
          "absolute top-1/2 -translate-y-1/2 transform",
          childClassName,
          style.icon,
          position === "left" ? "left-3.5" : "right-3.5",
        ),
      });
    };

    return (
      <div className="text-base">
        {label && (
          <label htmlFor={props.id} className={clsx("mb-2 block", style.label)}>
            {label}
          </label>
        )}
        <div className="relative">
          {isValidElement(leftIcon) && (
            <IconWrapper position="left">{leftIcon}</IconWrapper>
          )}
          {isValidElement(rightIcon) && (
            <IconWrapper position="right">{rightIcon}</IconWrapper>
          )}
          <input
            ref={ref}
            style={{
              // for some reason, the outline is not removed by the tailwind class
              outline: "unset",
            }}
            className={clsx(
              // px-3.5 is the same 14px inset the icons are positioned at below,
              // which is what the previous px-[14.5px] was approximating. The
              // icon-side padding clears a 14px inset plus the icon itself.
              "block h-10 w-full rounded-lg px-3.5 text-base disabled:cursor-not-allowed disabled:opacity-50",
              leftIcon && "pl-11",
              rightIcon && "pr-11",
              style.input,
              className,
            )}
            disabled={disabled}
            {...props}
          />
        </div>
        {
          // if the input is not neutral and there is a success or error message
          finalStatus !== "neutral" && (!!successMsg || !!errorMsg) && (
            <p className={clsx("mt-2", style.label)}>
              {errorMsg || successMsg}
            </p>
          )
        }
        {
          // if the input is neutral and there is a help message
          finalStatus === "neutral" && helpMsg && (
            <p className="mt-2 text-zinc-500">{helpMsg}</p>
          )
        }
      </div>
    );
  },
);
